import { useEffect, useState, type ComponentType, type FormEvent, type ReactNode } from 'react';
import { useLocale } from '@instamenu/context-i18n';
import { ArrowRight, CheckCheck, Languages, Sparkles, Wand2 } from 'lucide-react';
import { Box, Button, Sparkle } from '@instamenu/ui-system-next';
import type { Editor } from '@tiptap/react';
import {
  actionButton,
  actionIcon,
  promptBox,
  suggestionsColumn,
  suggestionsTitle,
  suggestionsList,
  suggestionButton,
  suggestionContent,
  promptInputs,
  promptSendButton,
  tidyingIcon,
  suggestionLabel,
  suggestionIcon,
} from './tiptap-bubble-menu.css';
import { AutoResizeTextarea } from '../auto-resize-textarea';
import { messageEditTextarea } from '../message-edit.css';
import { Text } from '@instamenu/ui-system-next';

type TiptapBubbleAiProps = {
  editor: Editor;
  isNotepadAiEditing: boolean;
  onAsk: (prompt: string) => void;
  children?: (state: { isOpen: boolean }) => ReactNode;
};

type SuggestionKey = 'improve' | 'correct' | 'simplify' | 'translate';

type Suggestion = {
  key: SuggestionKey;
  label: string;
  prompt: string;
};

const suggestionIconMap: Record<SuggestionKey, ComponentType<{ className?: string }>> = {
  improve: Wand2,
  correct: CheckCheck,
  simplify: Sparkles,
  translate: Languages,
};

export function TiptapBubbleAi({
  editor,
  onAsk,
  children,
  isNotepadAiEditing,
}: TiptapBubbleAiProps) {
  const { i18n } = useLocale();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [prompt, setPrompt] = useState('');

  const resetPrompt = () => {
    setIsPromptOpen(false);
    setPrompt('');
  };

  useEffect(() => {
    const handleBlur = () => {
      resetPrompt();
    };

    editor.on('blur', handleBlur);

    return () => {
      editor.off('blur', handleBlur);
    };
  }, [editor]);

  const handleAsk = (event?: FormEvent) => {
    event?.preventDefault();
    const ask = prompt.trim();
    resetPrompt();
    onAsk(ask);
  };

  const suggestions: Suggestion[] = [
    {
      key: 'improve',
      label: i18n.t('aiAssistant.suggestions.improveWriting'),
      prompt: 'Improve the writing and flow without changing meaning.',
    },
    {
      key: 'correct',
      label: i18n.t('aiAssistant.suggestions.correct'),
      prompt: 'Fix grammar, spelling, and punctuation while keeping the tone.',
    },
    {
      key: 'simplify',
      label: i18n.t('aiAssistant.suggestions.simplify'),
      prompt: 'Rewrite in simpler language while preserving key details.',
    },
    {
      key: 'translate',
      label: i18n.t('aiAssistant.suggestions.translate'),
      prompt: '',
    },
  ];

  const handleTranslateSuggestion = () => {
    setPrompt('Translate to ');
    setIsPromptOpen(true);
  };

  const handleSuggestion = (value: string) => {
    if (!value.trim()) return;
    resetPrompt();
    onAsk(value.trim());
  };

  const handleOpenPrompt = () => {
    setIsPromptOpen(true);
  };

  return (
    <>
      {isPromptOpen && (
        <form onSubmit={handleAsk}>
          <div className={promptBox}>
            <div className={promptInputs}>
              <AutoResizeTextarea
                autoFocus
                className={messageEditTextarea}
                style={{ flex: 1 }}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={i18n.t('aiAssistant.placeholder')}
              />

              <Button
                type="submit"
                size="xs"
                aria-label={i18n.t('generics.send')}
                title={i18n.t('generics.send')}
                className={promptSendButton}
              >
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>

          <div className={suggestionsColumn}>
            <div className={suggestionsTitle}>{i18n.t('aiAssistant.suggestions.title')}</div>
            <div className={suggestionsList}>
              {suggestions.map((item) => (
                <Box
                  key={item.key}
                  className={suggestionButton}
                  onClick={
                    item.key === 'translate'
                      ? handleTranslateSuggestion
                      : () => handleSuggestion(item.prompt)
                  }
                >
                  <Box className={suggestionContent}>
                    {(() => {
                      const Icon = suggestionIconMap[item.key];
                      return <Icon className={suggestionIcon} aria-hidden="true" />;
                    })()}
                    <Text className={suggestionLabel}>{item.label}</Text>
                  </Box>
                </Box>
              ))}
            </div>
          </div>
        </form>
      )}
      {!isPromptOpen &&
        (isNotepadAiEditing ? (
          <Wand2 className={`${actionIcon} ${tidyingIcon}`} />
        ) : (
          <button type="button" className={actionButton} onClick={handleOpenPrompt}>
            <Sparkle className={actionIcon} style={{ backgroundColor: 'transparent' }} />
          </button>
        ))}
      {children?.({ isOpen: isPromptOpen })}
    </>
  );
}
